import { Link, useLocation } from 'react-router-dom';
import { motion } from 'motion/react';
import { Icon, type IconName } from './Icon';

export interface BottomNavItem {
  icon: IconName;
  label: string;
  to: string;
}

interface BottomNavProps {
  items: BottomNavItem[];
}

function isActivePath(pathname: string, to: string): boolean {
  if (to === '/') return pathname === '/';
  return pathname === to || pathname.startsWith(`${to}/`);
}

// v7 Forest Glass dock — 화면 하단에 떠 있는 글래스 필, 활성 탭 필이 슬라이드
export function BottomNav({ items }: BottomNavProps) {
  const { pathname } = useLocation();
  return (
    <nav
      role="navigation"
      aria-label="주요 내비게이션"
      className="fixed bottom-[max(env(safe-area-inset-bottom),14px)] left-1/2 z-40 flex -translate-x-1/2 items-center gap-1 rounded-full border border-surface-border/80 bg-surface/90 p-1.5 shadow-[var(--shadow-dock)] backdrop-blur-xl"
    >
      {items.map((item) => {
        const active = isActivePath(pathname, item.to);
        return (
          <Link
            key={item.to}
            to={item.to}
            aria-current={active ? 'page' : undefined}
            aria-label={item.label}
            className={`pressable relative flex min-h-[46px] items-center justify-center gap-1.5 rounded-full px-6 text-[13.5px] font-semibold focus-visible:outline-none focus-visible:shadow-focus-ring ${
              active ? 'text-white' : 'text-ink-faint hover:text-ink-muted'
            }`}
          >
            {active ? (
              <motion.span
                layoutId="bottom-nav-pill"
                aria-hidden="true"
                className="absolute inset-0 rounded-full bg-[image:var(--hero-grad)] shadow-[var(--hero-glow)]"
                transition={{ type: 'spring', stiffness: 420, damping: 34 }}
              />
            ) : null}
            <Icon name={item.icon} size={18} className="relative shrink-0" />
            <span className="relative truncate">{item.label}</span>
          </Link>
        );
      })}
    </nav>
  );
}
